// Staff plan import draft types + helpers. Uploads still use the stub roster
// until the AI parser (see `buildStaffPlanParserRequest` in @rc/engine) is wired.
// Review works at individual-role grain; Summary/Save aggregates to the matrix.

import type {
  LevelId,
  Model,
  StaffPlanCell,
  StaffPlanImportAiConfidence,
  StaffPlanImportRow,
  StaffPlanOtherRole,
  StaffPlanRoleCategory,
} from '@rc/engine/types';

export type { StaffPlanImportAiConfidence, StaffPlanImportRow, StaffPlanRoleCategory };

export type StaffPlanImportStep = 'upload' | 'review' | 'summary';
export type StaffPlanImportStatus = 'draft' | 'saved';

/** Standard roles map to a model discipline; Other roles carry a free-text role label. */
export const ROLE_CATEGORIES: Array<{ id: StaffPlanRoleCategory; label: string }> = [
  { id: 'standard', label: 'Standard' },
  { id: 'other', label: 'Other' },
];

/** Mapped fields required for aggregation (plus hub loc when Hub = Yes). */
export type StaffPlanMappedFieldKey =
  | 'levelId'
  | 'roleCategory'
  | 'disciplineId'
  | 'hubId';

/** Which classification fields are still missing / invalid for this row. */
export function missingMappedFields(row: StaffPlanImportRow): StaffPlanMappedFieldKey[] {
  const missing: StaffPlanMappedFieldKey[] = [];
  if (!row.levelId) missing.push('levelId');
  if (!row.roleCategory) missing.push('roleCategory');
  const hasDiscipline =
    row.roleCategory === 'other' ? !!row.otherRole?.trim() : !!row.disciplineId;
  if (!hasDiscipline) missing.push('disciplineId');
  if (row.hub && !row.hubId) missing.push('hubId');
  return missing;
}

export function isMappedFieldMissing(
  row: StaffPlanImportRow,
  field: StaffPlanMappedFieldKey,
): boolean {
  return missingMappedFields(row).includes(field);
}

export interface StaffPlanImportDraft {
  id: string;
  clientId: string;
  /** Target plan id, or `'new'` when creating. */
  planId: string;
  modelId: string;
  step: StaffPlanImportStep;
  status: StaffPlanImportStatus;
  fileName?: string;
  rows: StaffPlanImportRow[];
  updatedAt: string;
}

function makeId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${rand}`;
}

/** Bump key when row shape changes so stale drafts don't break the UI. */
function storageKey(clientId: string, planId: string): string {
  return `gia.staffPlanImport.v6.${clientId}.${planId}`;
}

export function loadImportDraft(clientId: string, planId: string): StaffPlanImportDraft | null {
  try {
    const raw = localStorage.getItem(storageKey(clientId, planId));
    if (!raw) return null;
    return JSON.parse(raw) as StaffPlanImportDraft;
  } catch {
    return null;
  }
}

export function saveImportDraft(draft: StaffPlanImportDraft): void {
  const next = { ...draft, updatedAt: new Date().toISOString() };
  localStorage.setItem(storageKey(draft.clientId, draft.planId), JSON.stringify(next));
}

export function clearImportDraft(clientId: string, planId: string): void {
  localStorage.removeItem(storageKey(clientId, planId));
}

export function createEmptyDraft(
  clientId: string,
  planId: string,
  modelId: string,
): StaffPlanImportDraft {
  return {
    id: makeId('imp'),
    clientId,
    planId,
    modelId,
    step: 'upload',
    status: 'draft',
    rows: [],
    updatedAt: new Date().toISOString(),
  };
}

export function isRowClassified(row: StaffPlanImportRow): boolean {
  return missingMappedFields(row).every((f) => f === 'hubId');
}

function isOtherRow(row: StaffPlanImportRow): boolean {
  return row.roleCategory === 'other';
}

function findDiscipline(model: Model, ...hints: string[]): string | undefined {
  const discs = model.disciplines.filter((d) => d.active !== false);
  for (const hint of hints) {
    const h = hint.toLowerCase();
    const hit = discs.find(
      (d) => d.id.toLowerCase().includes(h) || d.label.toLowerCase().includes(h),
    );
    if (hit) return hit.id;
  }
  return undefined;
}

function findChannel(model: Model, label?: string): string {
  if (!label || label.toLowerCase() === 'all') return 'all';
  const hit = model.channels.find(
    (c) =>
      c.active !== false &&
      (c.label.toLowerCase() === label.toLowerCase() ||
        c.id.toLowerCase() === label.toLowerCase().replace(/\s+/g, '_')),
  );
  return hit?.id || 'all';
}

function findHubId(model: Model, location?: string): string | undefined {
  if (!location) return undefined;
  const hubs = model.ogsHubs ?? [];
  const loc = location.toLowerCase();
  return hubs.find(
    (h) =>
      h.label.toLowerCase() === loc ||
      h.id.toLowerCase() === loc ||
      h.label.toLowerCase().includes(loc),
  )?.id;
}

/** Deterministic roster stub — individual people, not matrix cells. */
export function stubParseStaffPlan(model: Model, fileName: string): StaffPlanImportRow[] {
  const samples: Array<{
    name: string;
    email: string;
    title: string;
    sourceFunction: string;
    sourceChannel: string;
    fte: number;
    sourceOgsHubbed: boolean;
    sourceHubLocation: string;
    levelId?: LevelId;
    roleCategory?: StaffPlanRoleCategory;
    otherRole?: string;
    disciplineHints: string[];
    mappedChannel?: string;
    hub?: boolean;
    hubLocation?: string;
    forceUnmapped?: boolean;
    aiConfidence?: StaffPlanImportAiConfidence;
    aiComment?: string;
  }> = [
    {
      name: 'Alex Rivera',
      email: 'alex.rivera@example.com',
      title: 'Media Planner',
      sourceFunction: 'Planning',
      sourceChannel: 'All',
      fte: 1,
      sourceOgsHubbed: false,
      sourceHubLocation: '',
      levelId: 'L3',
      roleCategory: 'standard',
      disciplineHints: ['plan'],
      hub: false,
      aiConfidence: 'high',
      aiComment: 'Title + function match Planning; Mid-Level → L3.',
    },
    {
      name: 'Jordan Lee',
      email: 'jordan.lee@example.com',
      title: 'Senior Media Planner',
      sourceFunction: 'Planning',
      sourceChannel: 'All',
      fte: 1,
      sourceOgsHubbed: false,
      sourceHubLocation: 'New York',
      levelId: 'L5',
      roleCategory: 'standard',
      disciplineHints: ['plan'],
      hub: false,
      aiConfidence: 'high',
      aiComment: '“Senior” seniority cue → L5; onshore NY.',
    },
    {
      name: 'Priya Shah',
      email: 'priya.shah@example.com',
      title: 'Paid Social Specialist',
      sourceFunction: 'Investment / Digital',
      sourceChannel: 'Paid Social',
      fte: 1,
      sourceOgsHubbed: true,
      sourceHubLocation: 'Bangalore',
      levelId: 'L3',
      roleCategory: 'standard',
      disciplineHints: ['invest', 'plan'],
      mappedChannel: 'Paid Social',
      hub: true,
      hubLocation: 'Bangalore',
      aiConfidence: 'medium',
      aiComment:
        'Function spans Investment/Digital — preferred Investment. Hub from Bangalore OGS flag; confirm hub id on model.',
    },
    {
      name: 'Sam Okonkwo',
      email: 'sam.okonkwo@example.com',
      title: 'Investment Manager',
      sourceFunction: 'Investment',
      sourceChannel: 'Search',
      fte: 1,
      sourceOgsHubbed: false,
      sourceHubLocation: 'Chicago',
      levelId: 'L5',
      roleCategory: 'standard',
      disciplineHints: ['invest'],
      mappedChannel: 'Search',
      hub: false,
      aiConfidence: 'high',
      aiComment: 'Direct Investment + Search channel match; Manager → L5.',
    },
    {
      name: 'Casey Nguyen',
      email: 'casey.nguyen@example.com',
      title: 'Analytics Lead',
      sourceFunction: 'Analytics',
      sourceChannel: 'All',
      fte: 0.5,
      sourceOgsHubbed: false,
      sourceHubLocation: 'Remote US',
      levelId: 'L6',
      roleCategory: 'standard',
      disciplineHints: ['analyt'],
      hub: false,
      aiConfidence: 'medium',
      aiComment:
        '“Lead” could be L5 or L6 — chose L6. Remote US treated as onshore; confirm if hubbed.',
    },
    {
      name: 'Taylor Brooks',
      email: 'taylor.brooks@example.com',
      title: 'Influencer Coordinator',
      sourceFunction: 'Influencer',
      sourceChannel: 'Social',
      fte: 1,
      sourceOgsHubbed: false,
      sourceHubLocation: 'Los Angeles',
      levelId: 'L2',
      roleCategory: 'other',
      otherRole: 'Influencer',
      disciplineHints: [],
      hub: false,
      aiConfidence: 'medium',
      aiComment:
        'Influencer has no model discipline — classified as Other / Influencer. Shown with the plan, not calculated.',
    },
    {
      name: 'Riley Chen',
      email: 'riley.chen@example.com',
      title: 'Unmapped Client Role',
      sourceFunction: 'Special Projects',
      sourceChannel: 'Other',
      fte: 1,
      sourceOgsHubbed: false,
      sourceHubLocation: 'Unknown',
      roleCategory: 'other',
      disciplineHints: [],
      forceUnmapped: true,
      hub: false,
      aiConfidence: 'low',
      aiComment:
        'No discipline or level match for “Special Projects”. Set as Other — name the role and level, or exclude.',
    },
  ];

  return samples.map((s) => {
    const roleCategory = s.roleCategory || 'standard';
    const isOther = roleCategory === 'other';
    const disciplineId =
      s.forceUnmapped || isOther ? undefined : findDiscipline(model, ...s.disciplineHints);
    const channelId = isOther
      ? undefined
      : findChannel(model, s.mappedChannel || s.sourceChannel);
    const levelId = s.levelId;
    const unmapped =
      s.forceUnmapped || !levelId || (isOther ? !s.otherRole : !disciplineId);

    const wantsHub = s.hub ?? false;
    const hubId = wantsHub
      ? findHubId(model, s.hubLocation || s.sourceHubLocation) || model.ogsHubs?.[0]?.id
      : undefined;
    const hubMeta = hubId ? model.ogsHubs?.find((h) => h.id === hubId) : undefined;

    let aiComment = s.aiComment;
    let aiConfidence = s.aiConfidence;
    if (unmapped && !aiComment) {
      aiConfidence = 'low';
      aiComment = 'Classification incomplete — missing discipline and/or level.';
    } else if (!isOther && !disciplineId && !s.forceUnmapped) {
      aiConfidence = aiConfidence || 'low';
      aiComment =
        aiComment ||
        `Could not resolve discipline from hints (${s.disciplineHints.join(', ') || 'none'}).`;
    }

    return {
      id: makeId('row'),
      include: !unmapped,
      name: s.name,
      email: s.email,
      title: s.title,
      sourceFunction: s.sourceFunction,
      sourceChannel: s.sourceChannel,
      sourceFte: s.fte,
      sourceOgsHubbed: s.sourceOgsHubbed,
      sourceHubLocation: s.sourceHubLocation || undefined,
      sourceSheet: fileName || 'Sheet1',
      levelId: unmapped && s.forceUnmapped ? undefined : levelId,
      roleCategory,
      disciplineId,
      otherRole: s.otherRole,
      channelId: unmapped && s.forceUnmapped ? undefined : channelId,
      mappedFte: s.fte,
      hub: wantsHub && !!hubId,
      hubId,
      hubLocation: hubMeta?.label,
      unmapped,
      aiConfidence,
      aiComment,
    };
  });
}

export function rowMappedFte(row: StaffPlanImportRow): number {
  const m = Number(row.mappedFte);
  if (!Number.isNaN(m) && row.mappedFte != null) return m;
  return Number(row.sourceFte) || 0;
}

export function importTotals(rows: StaffPlanImportRow[]): {
  source: number;
  mapped: number;
  included: number;
  unmapped: number;
  people: number;
} {
  let source = 0;
  let mapped = 0;
  let included = 0;
  let unmapped = 0;
  for (const r of rows) {
    source += Number(r.sourceFte) || 0;
    if (!isRowClassified(r) || r.unmapped) unmapped += Number(r.sourceFte) || 0;
    if (!r.include) continue;
    included += rowMappedFte(r);
    if (isRowClassified(r)) mapped += rowMappedFte(r);
  }
  return { source, mapped, included, unmapped, people: rows.length };
}

/**
 * Aggregate included, classified individuals → StaffPlan cells.
 * Uses exact levelId; Hub Yes → hubFte, else onFte.
 */
export function aggregateImportRowsToCells(rows: StaffPlanImportRow[]): StaffPlanCell[] {
  const map = new Map<string, StaffPlanCell>();
  for (const r of rows) {
    if (!r.include || isOtherRow(r) || !isRowClassified(r) || !r.levelId || !r.disciplineId) {
      continue;
    }
    const channelId = r.channelId || 'all';
    const fte = rowMappedFte(r);
    if (!fte) continue;
    const toHub = !!r.hub;
    const onFte = toHub ? 0 : fte;
    const hubFte = toHub ? fte : 0;
    const key = `${r.disciplineId}::${channelId}::${r.levelId}`;
    const existing = map.get(key);
    if (existing) {
      existing.fte += fte;
      existing.onFte = (existing.onFte || 0) + onFte;
      existing.hubFte = (existing.hubFte || 0) + hubFte;
    } else {
      map.set(key, {
        disciplineId: r.disciplineId,
        channelId,
        levelId: r.levelId,
        fte,
        onFte,
        hubFte,
      });
    }
  }
  return [...map.values()];
}

/**
 * Aggregate included, classified Other-category individuals → `StaffPlan.otherRoles`
 * (role × level). Role labels are matched case-insensitively.
 */
export function aggregateImportRowsToOtherRoles(rows: StaffPlanImportRow[]): StaffPlanOtherRole[] {
  const map = new Map<string, StaffPlanOtherRole>();
  for (const r of rows) {
    const role = r.otherRole?.trim();
    if (!r.include || !isOtherRow(r) || !isRowClassified(r) || !r.levelId || !role) continue;
    const fte = rowMappedFte(r);
    if (!fte) continue;
    const onFte = r.hub ? 0 : fte;
    const hubFte = r.hub ? fte : 0;
    const key = `${role.toLowerCase()}::${r.levelId}`;
    const existing = map.get(key);
    if (existing) {
      existing.fte += fte;
      existing.onFte = (existing.onFte || 0) + onFte;
      existing.hubFte = (existing.hubFte || 0) + hubFte;
    } else {
      map.set(key, { role, levelId: r.levelId, fte, onFte, hubFte });
    }
  }
  return [...map.values()];
}

export function summarizeByDiscipline(
  cells: StaffPlanCell[],
  model: Model,
): Array<{ id: string; label: string; fte: number }> {
  const totals = new Map<string, number>();
  for (const c of cells) {
    totals.set(c.disciplineId, (totals.get(c.disciplineId) || 0) + c.fte);
  }
  return [...totals.entries()]
    .map(([id, fte]) => ({
      id,
      label: model.disciplines.find((d) => d.id === id)?.label || id,
      fte,
    }))
    .sort((a, b) => b.fte - a.fte);
}

export function emptyImportRow(model: Model): StaffPlanImportRow {
  const d0 = model.disciplines.find((d) => d.active !== false);
  return {
    id: makeId('row'),
    include: true,
    name: '',
    email: '',
    title: '',
    sourceFunction: '',
    sourceChannel: '',
    sourceFte: 1,
    sourceOgsHubbed: false,
    levelId: 'L3',
    roleCategory: 'standard',
    disciplineId: d0?.id,
    channelId: 'all',
    mappedFte: 1,
    hub: false,
    unmapped: !d0,
  };
}
